import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { NextPage } from 'next';
import React, { useMemo } from 'react';

import { PageTitle } from '@app/components/PageTitle';

import { APP_NAME } from '@app/constants/common';
import { FeatureFlagWrapper } from '@app/components/FeatureFlagWrapper';
import { SimpleValueWidget } from '@app/components/SimpleValueWidget';
import { TokenIcon } from '@app/components/TokenIcon';
import { Address } from '@thirdweb-dev/sdk';
import { useTokenBalanceV2 } from '@app/hooks/swap/useTokenBalance';
import { useTokenPriceV4 } from '@app/hooks/token/useTokenPriceV4';
import { formatUSD } from '@app/helpers/formatUSD';
import { useTotalAmmTvl } from '@app/hooks/other/useTotalAmmTvl';
import { useAmmRecentDailyStats } from '@app/hooks/stats/useAmmRecentDailyStats';
import { useKimInfo } from '@app/hooks/ryo/useKimInfo';
import { ethers } from 'ethers';
import { formatNumber, kFormatter } from '@app/helpers/format';
import { useDividentsStats } from '@app/hooks/ryo/useDividentsStats';
import { useBoosterStats } from '@app/hooks/booster/useBoosterStats';
import { useConfig } from '@app/config';
import { getTokensWhitelist } from '@app/constants/tokensWhitelist';
import Metadata from '@app/components/Metadata';
import AnimatedLayout from '@app/layouts/AnimatedLayout/AnimatedLayout';

export const InfoPage: NextPage = () => {
  const { data: totalAmmTvl, isLoading: isLoadingTotalTvl } = useTotalAmmTvl();
  const { data: stats, isLoading: isLoadingStats } = useAmmRecentDailyStats();
  const { data } = useKimInfo();
  const { totalAllocation } = useDividentsStats();
  const { data: booster, isLoading } = useBoosterStats();
  const config = useConfig();

  const preparedData = useMemo(() => {
    if (!data || !totalAllocation || !booster) {
      return;
    }

    const SUPPLY = [
      {
        label: 'Total supply',
        value: kFormatter(+ethers.utils.formatUnits(data.totalSupply, 18))
      },
      {
        label: 'Circulating supply',
        value: kFormatter(+ethers.utils.formatUnits(data.circulatingSupply, 18))
      },
      {
        label: 'Burn supply',
        value: kFormatter(+ethers.utils.formatUnits(data.burnSupply, 18))
      },
      {
        label: 'Max supply',
        value: kFormatter(+ethers.utils.formatUnits(data.maxSupply, 18))
      }
    ];

    const VALUATION = [
      {
        label: 'Price',
        value: formatUSD.format(+data.price)
      },
      {
        label: 'Market cap',
        value: kFormatter(+data.marketCap)
      },
      {
        label: 'Circulating market cap',
        value: kFormatter(+data.circulatingMarketcap)
      },
      {
        label: 'FDV',
        value: kFormatter(+data.fdv)
      }
    ];

    const SUPPLY_XKIM = [
      {
        label: 'Total supply',
        value: kFormatter(+ethers.utils.formatUnits(data.xKimTotalSupply, 18))
      },
      {
        label: 'Share of xKIM Supply',
        value: `${formatNumber(+data.xKimSupplyShare, 2)}%`
      },
      {
        label: 'Allocated Total',
        value: kFormatter(+totalAllocation + +booster.totalAllocation)
      }
    ];

    return {
      SUPPLY,
      VALUATION,
      SUPPLY_XKIM
    };
  }, [data, totalAllocation, booster]);

  return (
    <AnimatedLayout>
      <Metadata title={`Info | ${APP_NAME}`} />
      <FeatureFlagWrapper show={!!config?.FLAGS.showInfoPage}>
        <Box width="100%" mt={8} px={['12px', 7]}>
          <PageTitle
            title="Info"
            subtitle="KIM metrics overview including AMM stats and KIM/xKIM supplies and valuation"
          />
        </Box>
        <HStack
          mt={10}
          width="100%"
          flexWrap="wrap"
          gap={5}
          p="16px 24px"
          borderRadius="8px"
          border="1px solid"
          borderColor="gray.5"
          background="black"
        >
          <SimpleValueWidget
            label="AMM Total Liquidity"
            value={totalAmmTvl ? formatUSD.format(totalAmmTvl) : '$0'}
            isLoading={isLoadingTotalTvl}
            hasGrayBg={true}
            flex
            noBorder
          />
          <SimpleValueWidget
            label="Volume (24h)"
            value={stats?.volumeUSD ? formatUSD.format(+stats.volumeUSD) : '$0'}
            isLoading={isLoadingStats}
            hasGrayBg={true}
            flex
            noBorder
          />
          <SimpleValueWidget
            label="24h fees"
            value={stats?.feesUSD ? formatUSD.format(+stats.feesUSD) : '$0'}
            isLoading={isLoadingStats}
            hasGrayBg={true}
            flex
            noBorder
          />
        </HStack>

        <Flex
          padding={['12px', '28px']}
          borderRadius="16px"
          bg="gray.3"
          border="1px solid"
          borderColor="whiteOpacity.6"
          flexDirection="column"
          gap="24px"
          mt={8}
        >
          <TokenHeader tokenAddress={config?.CONTRACTS.KIM!} />
          <InfoRow title="Supply" items={preparedData?.SUPPLY} />
          <InfoRow title="Valuation" items={preparedData?.VALUATION} />

          <TokenHeader tokenAddress={config?.CONTRACTS.X_KIM!} />
          <InfoRow title="Supply" items={preparedData?.SUPPLY_XKIM} />
        </Flex>
      </FeatureFlagWrapper>
    </AnimatedLayout>
  );
};

export default InfoPage;

const InfoRow = ({
  title,
  items
}: {
  title: string;
  items?: {
    label: string;
    value: string | number;
  }[];
}) => {
  return (
    <HStack
      gap={2}
      p="16px 24px"
      borderRadius="8px"
      border="1px solid"
      borderColor="gray.5"
      background="black"
      flexDirection={['column', 'column', 'row']}
    >
      <Flex width={['100%', '100%', '20%']}>
        <Text fontSize={24} fontWeight={500} color="orange">
          {title}
        </Text>
      </Flex>
      {items?.map(item => {
        return (
          <Flex
            width={['100%', '100%', '20%']}
            flexDirection="column"
            key={item.label}
            gap="8px"
          >
            <Text fontSize={16} fontWeight={400} color="gray.15">
              {item.label}
            </Text>
            <Text fontSize={24} fontWeight={500} color="white">
              {item.value}
            </Text>
          </Flex>
        );
      })}
    </HStack>
  );
};

const TokenHeader = ({ tokenAddress }: { tokenAddress: Address }) => {
  const config = useConfig();

  const TOKENS_WHITELIST = getTokensWhitelist(config);

  const token =
    tokenAddress === config?.CONTRACTS.X_KIM
      ? {
          contractAddress: tokenAddress,
          symbol: 'xKIM'
        }
      : TOKENS_WHITELIST.find(
          item =>
            item.contractAddress.toLowerCase() === tokenAddress.toLowerCase()
        );

  const { data: balance } = useTokenBalanceV2(tokenAddress);

  const usdPrice = useTokenPriceV4(tokenAddress, balance?.displayValue ?? 0);

  if (!token) {
    return null;
  }

  return (
    <HStack width="100%" alignItems="center" justifyContent="space-between">
      <HStack gap={2}>
        <TokenIcon symbol={token.symbol} width="32px" height="32px" />
        <Text fontSize="28px" fontWeight={700}>
          {token.symbol}
        </Text>
      </HStack>
      <VStack gap={0} alignItems="flex-end">
        <Text fontSize={16} fontWeight={400} color="gray.15">
          Your balance
        </Text>
        <Text fontSize={24} fontWeight={500} color="orange">
          {balance?.displayValue ?? 0} {token.symbol}
        </Text>
        <Text fontSize={16} fontWeight={400} color="white">
          {usdPrice ? formatUSD.format(usdPrice) : '$0'}
        </Text>
      </VStack>
    </HStack>
  );
};
